// Pure display-only parsing helpers for blueprint rendering.
// These functions are used for visual presentation only — they do not affect
// generation, storage, or the output schema.

export type ParsedTestScenario =
  | { type: "structured"; setup: string; action: string; expectedResult: string }
  | { type: "full"; text: string };

export type ParsedEdgeCase =
  | { type: "split"; situation: string; handling: string }
  | { type: "full"; text: string };

export type ParsedDataModelItem =
  | { type: "entity"; name: string; subtitle: string | null; body: string }
  | { type: "raw"; text: string };

export type PromptSection = {
  heading: string;
  body: string;
};

/**
 * Tries to parse a test scenario string into Setup / Action / Expected Result parts.
 * Handles plain text and markdown-bold formats.
 * Falls back to { type: "full" } if the structure cannot be detected.
 */
export function parseTestScenario(text: string): ParsedTestScenario {
  if (!text.trim()) {
    return { type: "full", text };
  }

  // Strip markdown bold markers: **Setup** → Setup
  const cleaned = text.replace(/\*\*([^*]*)\*\*/g, "$1");

  const setupMatch = cleaned.match(/\bSetup\s*:\s*([^]+?)(?=\s*\bAction\s*:)/i);
  const actionMatch = cleaned.match(
    /\bAction\s*:\s*([^]+?)(?=\s*\bExpected\s+Result\s*:)/i,
  );
  const resultMatch = cleaned.match(/\bExpected\s+Result\s*:\s*([^]+?)$/i);

  const setup = setupMatch?.[1]?.trim();
  const action = actionMatch?.[1]?.trim();
  const expectedResult = resultMatch?.[1]?.trim();

  if (setup && action && expectedResult) {
    return { type: "structured", setup, action, expectedResult };
  }

  return { type: "full", text };
}

/**
 * Tries to split an edge case string into Situation / Expected handling
 * when separated by an em dash (–) or en dash (—).
 * Conservative: only splits on unambiguous delimiters with sufficient content on both sides.
 * Falls back to { type: "full" } if no delimiter is found.
 */
export function parseEdgeCase(text: string): ParsedEdgeCase {
  const trimmed = text.trim();
  if (!trimmed) {
    return { type: "full", text: trimmed };
  }

  // Only split on em dash / en dash surrounded by spaces, with ≥10 chars before and ≥5 after
  const dashMatch = trimmed.match(/^(.{10,}?)\s[–—]\s(.{5,})$/s);
  if (dashMatch) {
    return {
      type: "split",
      situation: dashMatch[1].trim(),
      handling: dashMatch[2].trim(),
    };
  }

  return { type: "full", text: trimmed };
}

/**
 * Tries to detect an entity/label name at the start of a data model item.
 * Matches: "EntityName:" or "Entity Name (subtitle):" patterns where the name
 * starts with a capital letter and contains only letters and spaces.
 * Falls back to { type: "raw" } if no entity pattern is detected.
 */
export function parseDataModelItem(text: string): ParsedDataModelItem {
  // Strip leading bullet markers
  const stripped = text.replace(/^[-•*]\s*/, "").trim();
  if (!stripped) {
    return { type: "raw", text: stripped };
  }

  // Match "Entity Name" or "Entity Name (subtitle):" at the start of the string.
  // Name: starts with uppercase, letters and spaces only, 2–49 additional chars.
  const match = stripped.match(
    /^([A-Z][A-Za-z ]{1,48})(\s*\([^)]{1,60}\))?\s*:\s*(.+)$/s,
  );

  if (match) {
    return {
      type: "entity",
      name: match[1].trim(),
      subtitle: match[2] ? match[2].replace(/[()]/g, "").trim() : null,
      body: match[3].trim(),
    };
  }

  return { type: "raw", text: stripped };
}

/**
 * Tries to detect **ScreenName**: blocks in the interface section text.
 * Splits on lines that immediately follow a newline and start with **ScreenName**.
 * Falls back to { type: "plain" } if fewer than 2 screens are detected.
 * Preamble text before the first screen is silently dropped — it is not meaningful
 * without the screen structure and is still available via the full TextBlock fallback.
 */
export type ParsedInterfaceSection =
  | { type: "screens"; screens: Array<{ name: string; content: string }> }
  | { type: "plain"; text: string };

export function parseInterfaceScreens(text: string): ParsedInterfaceSection {
  const trimmed = text.trim();
  if (!trimmed) {
    return { type: "plain", text: trimmed };
  }

  // Split on newlines that are immediately followed by **ScreenName** (with optional leading space)
  const segments = trimmed.split(/\n(?=\s*\*\*[^*\n]+\*\*)/);

  if (segments.length < 2) {
    return { type: "plain", text };
  }

  const screens: Array<{ name: string; content: string }> = [];

  for (const segment of segments) {
    // Match: optional whitespace + **ScreenName** + optional colon + rest of string
    const match = segment.match(/^\s*\*\*([^*\n]+)\*\*:?\s*(.*)$/s);
    if (match) {
      const name = match[1].trim();
      const content = match[2].trim();
      if (name) {
        screens.push({ name, content });
      }
    }
    // Segments that don't start with **...** are preamble — skipped silently
  }

  if (screens.length < 2) {
    return { type: "plain", text };
  }

  return { type: "screens", screens };
}

/**
 * Splits a Build Prompt string into named sections using all-caps headings as delimiters.
 * A heading is detected as a line containing only uppercase letters, digits, and spaces,
 * at least 5 characters long.
 * Returns an empty array if fewer than 3 sections are detected (triggers raw <pre> fallback).
 * Old or unusual prompts that don't parse cleanly are never mangled.
 */
export function parseBuildPromptSections(prompt: string): PromptSection[] {
  if (!prompt.trim()) {
    return [];
  }

  const lines = prompt.split("\n");
  const sections: PromptSection[] = [];
  let currentHeading = "";
  let bodyLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    // All-caps heading: only uppercase letters, digits, spaces; at least 5 chars
    if (trimmed.length >= 5 && /^[A-Z][A-Z0-9 ]+$/.test(trimmed)) {
      if (currentHeading) {
        sections.push({
          heading: currentHeading,
          body: bodyLines.join("\n").trim(),
        });
      }
      currentHeading = trimmed;
      bodyLines = [];
    } else if (currentHeading) {
      bodyLines.push(line);
    }
  }

  if (currentHeading) {
    sections.push({
      heading: currentHeading,
      body: bodyLines.join("\n").trim(),
    });
  }

  // Require at least 3 sections to use structured rendering; otherwise fall back to raw
  return sections.length >= 3 ? sections : [];
}
